import { action, computed, makeObservable, observable } from 'mobx'

import {
  CalendricalType,
  IAttachment,
  IFormMaterial,
  IFormMaterialResource,
  IInspectionOptions,
  IPermitWithFieldsInput,
  ISharedSitePermit,
  ISiteLocation,
  ISitePermit,
  IStepSubmission,
  IStepSubmissionInput,
  IThread,
  PermitFieldType,
  SitePermitStatus,
  WorkflowStepType,
} from '~/client/graph'
import { isLinkedWorkflowLikeField } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import User from '~/client/src/shared/models/User'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import Guard from '~/client/src/shared/utils/Guard'
import { clearSnapshotUrls } from '~/client/src/shared/utils/locationSnapshot'
import { getWorkflowFieldKey } from '~/client/src/shared/utils/WorkflowFieldsHelper'

import {
  locationPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '../constants/PermitFieldTypeConstants'
import { getWorkflowDirectLink } from '../constants/workflowRoutes'
import Localization from '../localization/LocalizationManager'
import ProjectsStore from '../stores/domain/Projects.store'
import SharedSitePermitStore from '../stores/domain/SharedSitePermit.store'
import ProjectDateStore, {
  areIntervalTimesIntersects,
  isAfter,
} from '../stores/ui/ProjectDate.store'
import EventStyle from '../types/EventStyle'
import getWorkflowFieldsDto from '../utils/getWorkflowFieldsDto'
import { EMPTY_STRING } from '../utils/usefulStrings'
import { copyObjectDeep } from '../utils/util'
import BaseModel from './BaseModel'
import IPermitFieldDto from './IPermitFieldDto'
import PermitField from './PermitField'
import { getSharedProjectName } from './SharedInfo'
import Thread from './Thread'
import WorkflowType from './WorkflowType'

import Colors from '~/client/src/shared/theme.module.scss'

const userFieldTypes = [
  PermitFieldType.Assignee,
  PermitFieldType.Requester,
  PermitFieldType.Subscriber,
]
const activeStatuses = [SitePermitStatus.Accepted, SitePermitStatus.Active]
export const workflowDoneStatuses = [
  SitePermitStatus.Done,
  SitePermitStatus.Ended,
]

const FREQUENCY_DATES_LIMIT = 1000

export type IWorkflowDto = ISitePermit
export type IWorkflowWithFieldsInput = IPermitWithFieldsInput

export default class Workflow
  extends BaseModel<IWorkflowDto>
  implements IWorkflowDto
{
  public static fromSharedPermitDto(sharedPermit: ISharedSitePermit): Workflow {
    return new Workflow(
      sharedPermit.id,
      null,
      sharedPermit.status,
      sharedPermit.projectToId,
      new Date(sharedPermit.startDate).getTime(),
      new Date(sharedPermit.endDate).getTime(),
      sharedPermit.summary,
      null,
      null,
      [],
      [],
      null,
      null,
      false,
      null,
      0,
      0,
      EMPTY_STRING,
      `${Localization.translator.shared}: ${sharedPermit.summary
        .split('-')
        .at(-1)}`,
    )
  }

  public sharedFromToDisplay = (
    sharedSitePermitStore: SharedSitePermitStore,
    projectsStore: ProjectsStore,
  ): string => {
    const sharedPermit = sharedSitePermitStore.getById(this.id)
    return getSharedProjectName(
      sharedPermit.projectUrl,
      projectsStore,
      sharedPermit.projectFromId,
    )
  }

  public static fromDto(dto: IWorkflowDto) {
    const permit = new Workflow(
      dto.id,
      dto.typeId,
      dto.status,
      dto.projectId,
      dto.startDate,
      dto.endDate,
      dto.title,
      dto.categoryId,
      dto.initialType,
      dto.linkedIn || [],
      dto.linkedWorkflows || [],
      dto.currentWorkflowStepId,
      dto.workflowStepLevel,
      dto.isAutoActivationEnabled,
      dto.createdBy,
      dto.createdAt,
      dto.updatedAt,
      dto.threadId,
      dto.code,
      dto.specificInspectionOptions,
      dto.fields,
      dto.isDeleted,
      dto.isAllDay,
      dto.isLateRequest,
      dto.recurringSettingsId,
      dto.stepSubmissions,
      dto.stepRoundId,
    )

    return permit
  }

  public static getDirectLinkToInstance(projectCode: string, id: string) {
    return getWorkflowDirectLink(projectCode, id)
  }

  public id: string
  public categoryId: string
  public initialType: string
  public linkedIn: string[]
  @observable public linkedWorkflows: string[]
  @observable public typeId: string
  @observable public startDate: number
  @observable public endDate: number
  @observable public isAllDay: boolean
  @observable public title: string
  @observable public threadId?: string
  @observable public code: string
  @observable public specificInspectionOptions?: IInspectionOptions
  @observable public fields: PermitField[]
  @observable public isAutoActivationEnabled: boolean
  @observable public isLateRequest?: boolean
  @observable public recurringSettingsId?: string
  @observable public stepSubmissions: IStepSubmission[] = []
  /**
   * The round the current step is in. The server starts a new one with every status change, also
   * when it moves the workflow back to a step it was in before, so only the sign-offs given since
   * then count toward the step. Null until the first status change after rounds were kept.
   */
  @observable public stepRoundId: string = null
  @observable private _status: SitePermitStatus
  @observable private _currentWorkflowStepId: string
  @observable private _workflowStepLevel: number
  @observable public _createdBy: string

  public get createdBy(): string {
    return this._createdBy
  }

  public get status(): SitePermitStatus {
    return this._status
  }

  private set internalStatus(value: SitePermitStatus) {
    this._status = value
  }

  public get currentWorkflowStepId(): string {
    return this._currentWorkflowStepId
  }

  private set internalCurrentWorkflowStepId(value: string) {
    this._currentWorkflowStepId = value
  }

  public get workflowStepLevel(): number {
    return this._workflowStepLevel
  }

  private set internalWorkflowStepLevel(value: number) {
    this._workflowStepLevel = value
  }

  @computed
  private get fieldsByIdMap(): Record<string, PermitField> {
    return this.fields.reduce(
      (map, field) => {
        const key = getWorkflowFieldKey(
          field.fieldId,
          field.parentInstanceId,
          field.parentValueIndex,
        )
        map[key] = field
        return map
      },
      {} as Record<string, PermitField>,
    )
  }

  @computed
  public get mainFieldsByIdMap(): Record<string, PermitField> {
    return this.fields.reduce(
      (map, field) => {
        if (!field.parentInstanceId) {
          map[field.fieldId] = field
        }
        return map
      },
      {} as Record<string, PermitField>,
    )
  }

  public get isLate(): boolean {
    return (
      !this.isDeleted &&
      this.status !== SitePermitStatus.Deleted &&
      !this.isDraft &&
      !this.isTerminal &&
      !!this.endDate &&
      isAfter(new Date(), this.endDate)
    )
  }

  public get isDraft(): boolean {
    return this.status === SitePermitStatus.Draft
  }

  public get isRequestedOrSubmitted(): boolean {
    return (
      this.status === SitePermitStatus.Requested ||
      this.status === SitePermitStatus.Submitted
    )
  }

  public get isRequested(): boolean {
    return this.status === SitePermitStatus.Requested
  }

  public get isChanged(): boolean {
    return this.status === SitePermitStatus.Changed
  }

  public get isApproved(): boolean {
    return this.status === SitePermitStatus.Accepted
  }

  public get isDenied(): boolean {
    return this.status === SitePermitStatus.Denied
  }

  public get isFailed(): boolean {
    return this.status === SitePermitStatus.Failed
  }

  public get isDeniedOrFailed(): boolean {
    return this.isDenied || this.isFailed
  }

  public get isDone(): boolean {
    return workflowDoneStatuses.includes(this.status)
  }

  public get isTerminal(): boolean {
    return this.isDone || this.isDeniedOrFailed
  }

  public get isDoneOrDenied(): boolean {
    return this.isTerminal
  }

  public constructor(
    id: string,
    typeId: string,
    status: SitePermitStatus,
    public projectId: string,
    startDate: number,
    endDate: number,
    title: string,
    categoryId: string,
    initialType: string,
    linkedIn: string[],
    linkedWorkflows: string[],
    currentWorkflowStepId: string,
    workflowStepLevel: number,
    isAutoActivationEnabled: boolean,
    createdBy: string,
    createdAt: number = 0,
    updatedAt: number = 0,
    threadId?: string,
    code: string = '',
    specificInspectionOptions?: IInspectionOptions,
    fields?: IPermitFieldDto[],
    public isDeleted?: boolean,
    isAllDay?: boolean,
    isLateRequest?: boolean,
    recurringSettingsId?: string,
    stepSubmissions?: IStepSubmission[],
    stepRoundId?: string,
  ) {
    super(id)

    this.typeId = typeId
    this.startDate = startDate
    this.endDate = endDate
    this.title = title
    this.categoryId = categoryId
    this.initialType = initialType
    this.linkedIn = linkedIn
    this.linkedWorkflows = linkedWorkflows
    this.threadId = threadId
    this.code = code
    this.specificInspectionOptions = specificInspectionOptions
    this.isAutoActivationEnabled = isAutoActivationEnabled
    this.isAllDay = !!isAllDay
    this.isLateRequest = isLateRequest

    this.internalStatus = status
    this.internalWorkflowStepLevel = workflowStepLevel
    this.internalCurrentWorkflowStepId = currentWorkflowStepId

    this.recurringSettingsId = recurringSettingsId

    this.fields = fields?.map(field => PermitField.fromDto(field)) || []

    this._createdBy = createdBy

    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)

    this.stepSubmissions = (stepSubmissions || []).map(submission => ({
      ...submission,
      fields: submission.fields?.map(f => PermitField.fromDto(f)) || [],
    }))
    this.stepRoundId = stepRoundId ?? null

    makeObservable(this)
    Guard.requireAll({ projectId, startDate, endDate })
  }

  @action.bound
  public setThread(thread: Thread | IThread): void {
    if (!thread) {
      return
    }
    this.threadId = thread.id
  }

  public get codeAndTitleCaption(): string {
    return this.title ? `${this.code}: ${this.title}` : this.code
  }

  public getCategoryAndTypeCaption = (
    workflowTypesStore: WorkflowTypesStore,
    workflowCategoriesStore: WorkflowCategoriesStore,
  ): string => {
    const permitType = workflowTypesStore.getPermitTypeById(this.typeId)
    const categoryName = workflowCategoriesStore.getNameById(
      permitType?.categoryId,
      true,
    )
    return permitType?.name
      ? `${categoryName}: ${permitType.name}`
      : categoryName
  }

  public getCategoryCaption = (
    workflowCategoriesStore: WorkflowCategoriesStore,
  ): string => {
    const categoryName = workflowCategoriesStore.getNameById(
      this.categoryId,
      true,
    )
    return categoryName
  }

  public getCaption = (
    workflowTypesStore: WorkflowTypesStore,
    withoutCode: boolean = false,
  ): string => {
    const caption = withoutCode ? '' : this.code ? this.code + ': ' : ''

    if (this.title) {
      return caption + this.title
    }

    const permitType = workflowTypesStore.getPermitTypeById(this.typeId)
    return caption + permitType?.name || ''
  }

  public isActive = (formType: WorkflowType): boolean => {
    if (!formType) return false

    return formType.hasStartStep
      ? this.status === SitePermitStatus.Active
      : activeStatuses.includes(this.status)
  }

  public getTypeOfPermitType = (
    workflowTypesStore: WorkflowTypesStore,
  ): string => {
    return workflowTypesStore.getPermitTypeById(this.typeId)?.type
  }

  public getIconNameOfPermitType = (
    workflowTypesStore: WorkflowTypesStore,
  ): string => {
    return workflowTypesStore.getPermitTypeById(this.typeId)?.iconName
  }

  public getNameOfPermitType = (
    workflowTypesStore: WorkflowTypesStore,
  ): string => {
    return workflowTypesStore.getPermitTypeById(this.typeId)?.name
  }

  public isInspectionPermit = (
    workflowTypesStore: WorkflowTypesStore,
  ): boolean => {
    return workflowTypesStore.getPermitTypeById(this.typeId)?.isInspectionType
  }

  public getAllInspectionDates = (
    workflowTypesStore: WorkflowTypesStore,
    projectDateStore: ProjectDateStore,
    endDate?: Date | number,
  ): Date[] => {
    const permitType = workflowTypesStore.getPermitTypeById(this.typeId)
    const endDateToUse = endDate || this.endDate

    if (!permitType?.isInspectionType || !this.startDate || !endDateToUse) {
      return []
    }

    const {
      hasWorkingDays,
      isWorkingDay,
      startOfDay,
      addDays,
      addWorkingDays,
    } = projectDateStore

    const frequency = this.getInspectionFrequency(permitType)
    const inspectionFrequencyType = this.getInspectionFrequencyType(permitType)
    const selectedDaysToRepeat = this.getSelectedDaysToRepeat(permitType)
    const isWeeklyInspection = inspectionFrequencyType === CalendricalType.Week

    let nextInspectionDate = startOfDay(this.startDate)
    const dates: Date[] = []

    while (!isAfter(nextInspectionDate, endDateToUse)) {
      if (dates.length > FREQUENCY_DATES_LIMIT) {
        break
      }

      if (
        !isWeeklyInspection &&
        hasWorkingDays &&
        !isWorkingDay(nextInspectionDate)
      ) {
        nextInspectionDate = addDays(nextInspectionDate, 1)
        continue
      }

      if (!isWeeklyInspection) {
        dates.push(nextInspectionDate)
      }

      switch (inspectionFrequencyType) {
        case CalendricalType.Day:
          nextInspectionDate = addWorkingDays(nextInspectionDate, frequency)
          break

        case CalendricalType.Week:
          const inspectionDays = this.getCurrentWeeklyInspectionDates(
            selectedDaysToRepeat,
            projectDateStore,
            endDateToUse,
            nextInspectionDate,
          )

          dates.push(...inspectionDays)

          nextInspectionDate = this.getNextWeeklyInspectionDate(
            frequency,
            selectedDaysToRepeat,
            projectDateStore,
            nextInspectionDate,
          )
          break

        case CalendricalType.Month:
          nextInspectionDate = this.getNextMonthlyInspectionDate(
            frequency,
            nextInspectionDate,
            projectDateStore,
          )
          break

        default:
          return
      }
    }

    return dates
  }

  public getLatestInspectionDate = (
    workflowTypesStore: WorkflowTypesStore,
    projectDateStore: ProjectDateStore,
  ): Date => {
    const permitType = workflowTypesStore.getPermitTypeById(this.typeId)

    if (!permitType?.isInspectionType) {
      return null
    }

    const now = new Date()
    const endDateToUse = isAfter(this.endDate, now) ? now : this.endDate

    return this.getAllInspectionDates(
      workflowTypesStore,
      projectDateStore,
      endDateToUse,
    ).pop()
  }

  public isAssignedToUser = ({ id: userId }: User): boolean => {
    const ids = this.fields
      .filter(({ type }) => userFieldTypes.includes(type))
      .flatMap(({ values }) => values as string[])

    return ids.includes(userId)
  }

  public copy(): Workflow {
    const copiedObj = new Workflow(
      this.id,
      this.typeId,
      this.status,
      this.projectId,
      this.startDate,
      this.endDate,
      this.title,
      this.categoryId,
      this.initialType,
      this.linkedIn,
      this.linkedWorkflows,
      this.currentWorkflowStepId,
      this.workflowStepLevel,
      this.isAutoActivationEnabled,
      this.createdBy,
      this.createdAt,
      this.updatedAt,
      this.threadId,
      this.code,
      this.specificInspectionOptions &&
        copyObjectDeep(this.specificInspectionOptions),
      null,
      this.isDeleted,
      this.isAllDay,
      this.isLateRequest,
      this.recurringSettingsId,
    )
    copiedObj.fields = this.copyFields()
    copiedObj.stepSubmissions = this.copyStepSubmissions()
    copiedObj.stepRoundId = this.stepRoundId

    return copiedObj
  }

  public getCopyForDuplicate(
    status: SitePermitStatus,
    formType: WorkflowType,
  ): Workflow {
    const copy = new Workflow(
      null,
      formType.id,
      status,
      this.projectId,
      this.startDate,
      this.endDate,
      this.title,
      this.categoryId,
      this.initialType,
      this.linkedIn,
      this.linkedWorkflows,
      formType.initialStep.id,
      0,
      this.isAutoActivationEnabled,
      this.createdBy,
      null,
      null,
      null,
      null,
      copyObjectDeep(this.specificInspectionOptions),
      null,
      false,
      this.isAllDay,
      this.isLateRequest,
      this.recurringSettingsId,
    )
    copy.fields = this.copyFields()
    // Report snapshots belong to the source workflow's save — the duplicate
    // must capture its own on first save, not ship another workflow's images.
    // Table field values are row arrays of nested fields; recurse one level so
    // location cells inside tables are stripped too.
    copy.fields.forEach(field =>
      field.values?.forEach(value => {
        if (Array.isArray(value)) {
          value.forEach(cell => cell?.values?.forEach(clearSnapshotUrls))
        } else {
          clearSnapshotUrls(value)
        }
      }),
    )

    return copy
  }

  private copyFields(): PermitField[] {
    if (!this.fields?.length) return []

    return this.fields.map(field => copyObjectDeep(field))
  }

  private copyStepSubmissions(): IStepSubmission[] {
    if (!this.stepSubmissions?.length) return []

    return this.stepSubmissions.map(submission => ({
      stepId: submission.stepId,
      userId: submission.userId,
      ruleId: submission.ruleId,
      roundId: submission.roundId,
      submittedAt: submission.submittedAt,
      fields:
        submission.fields?.map(f => {
          if (f instanceof PermitField) {
            const clone = new PermitField(
              f.instanceId,
              f.fieldId,
              f.type,
              f.parentInstanceId,
              f.parentValueIndex,
            )
            clone.values = [...f.values]
            return clone
          }
          return { ...f }
        }) || [],
    }))
  }

  public getDto(message?: string): IWorkflowWithFieldsInput {
    return {
      permit: {
        id: this.id,
        typeId: this.typeId,
        status: this.status,
        projectId: this.projectId,
        startDate: this.startDate,
        endDate: this.endDate,
        title: this.title,
        threadId: this.threadId,
        code: this.code,
        specificInspectionOptions: this.specificInspectionOptions,
        currentWorkflowStepId: this.currentWorkflowStepId,
        workflowStepLevel: this.workflowStepLevel,
        isAutoActivationEnabled: this.isAutoActivationEnabled,
        isAllDay: this.isAllDay,
        linkedWorkflows: this.linkedWorkflows,
      },
      fields: getWorkflowFieldsDto(this.fields),
      stepSubmissions: this.getStepSubmissionsDto(),
      message: message ? { text: message } : null,
    }
  }

  private getStepSubmissionsDto(): IStepSubmissionInput[] | null {
    if (!this.stepSubmissions?.length) return null

    return this.stepSubmissions.map(submission => ({
      stepId: submission.stepId,
      userId: submission.userId,
      ruleId: submission.ruleId,
      roundId: submission.roundId,
      submittedAt: submission.submittedAt,
      fields: getWorkflowFieldsDto((submission.fields as PermitField[]) || []),
    }))
  }

  public getReportDto(): ISitePermit {
    return {
      id: this.id,
      typeId: this.typeId,
      status: this.status,
      projectId: this.projectId,
      startDate: this.startDate,
      endDate: this.endDate,
      title: this.title,
      threadId: this.threadId,
      code: this.code,
      specificInspectionOptions: this.specificInspectionOptions,
      currentWorkflowStepId: this.currentWorkflowStepId,
      workflowStepLevel: this.workflowStepLevel,
      isAutoActivationEnabled: this.isAutoActivationEnabled,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      fields: this.fields || [],
      stepSubmissions: this.stepSubmissions || [],
      stepRoundId: this.stepRoundId,
      isDeleted: this.isDeleted,
      createdBy: this.createdBy,
      linkedWorkflows: this.linkedWorkflows,
    }
  }

  @computed
  public get allLinkedWorkflowIds(): string[] {
    return [
      ...new Set([
        ...this.linkedIn,
        ...this.linkedWorkflows,
        ...this.linkedWorkflowFields,
      ]),
    ]
  }

  public get specificInspectionFrequency(): number {
    return this.specificInspectionOptions?.inspectionFrequency || 0
  }

  public get companyIds(): string[] {
    return this.getFieldValuesByType<string>(PermitFieldType.Company)
  }

  public get responsibleIds(): string[] {
    return this.getFieldValuesByType<string>(PermitFieldType.Assignee)
  }

  public get requesterIds(): string[] {
    return this.getFieldValuesByType<string>(PermitFieldType.Requester)
  }

  public get subscriberIds(): string[] {
    return this.getFieldValuesByType<string>(PermitFieldType.Subscriber)
  }

  public get locations(): ISiteLocation[] {
    return this.getFieldValuesByType<ISiteLocation>(PermitFieldType.Location)
  }

  public get attachments(): IAttachment[] {
    return this.getFieldValuesByType<IAttachment>(PermitFieldType.File)
  }

  public get equipment(): ISiteLocation[] {
    return this.getFieldValuesByType<ISiteLocation>(PermitFieldType.Equipment)
  }

  @computed
  public get linkedWorkflowFields(): string[] {
    const allFields = [
      ...this.fields,
      ...this.fields
        .filter(f => f.type === PermitFieldType.Table)
        .flatMap(f => f.values.flat()),
    ]
    return allFields
      .filter(f => isLinkedWorkflowLikeField(f.type))
      .flatMap(({ values }) => values || [])
  }

  public get locationsWithEquipment(): ISiteLocation[] {
    return this.locations.concat(this.equipment)
  }

  // Don't broaden `locations`/`equipment` — many consumers rely on
  // their primary-only scoping.
  @computed
  public get allLocationFieldValues(): ISiteLocation[] {
    const values = locationPermitFieldTypes
      .flatMap(t => this.getAllCurrentAndSubmittedFieldsByType(t))
      .flatMap(field => (field.values || []) as ISiteLocation[])
    return Array.from(
      new Map(values.filter(v => v?.id).map(v => [v.id, v])).values(),
    )
  }

  public get materials(): IFormMaterial[] {
    return this.getFieldValuesByType<IFormMaterial>(PermitFieldType.Material)
  }

  // Location ids of the selected bookable material resources (MaterialResource
  // field values key on locationId, not id). Used by resource restriction
  // validation, which otherwise only iterates allLocationFieldValues. Current,
  // submitted-step, and Table-nested fields are included, matching how
  // allLocationFieldValues collects location/equipment resource values.
  @computed
  public get materialResourceLocationIds(): string[] {
    return materialResourcePermitFieldTypes
      .flatMap(type => this.getAllCurrentAndSubmittedFieldsByType(type))
      .flatMap(field => (field.values || []) as IFormMaterialResource[])
      .map(value => value?.locationId)
      .filter((id): id is string => !!id)
  }

  public get materialFields(): PermitField[] {
    return this.getAllFieldsByType(PermitFieldType.Material)
  }

  public getAllFieldsByType = (type: PermitFieldType): PermitField[] => {
    const allFields = [
      ...this.fields,
      ...this.fields
        .filter(f => f.type === PermitFieldType.Table)
        .flatMap(f => f.values.flat()),
    ]
    return allFields.filter(f => type === f.type)
  }

  private getAllCurrentAndSubmittedFieldsByType(
    type: PermitFieldType,
  ): PermitField[] {
    const topLevelFields = [
      ...this.fields,
      ...this.stepSubmissions.flatMap(
        submission => (submission.fields || []) as PermitField[],
      ),
    ]
    const allFields = [
      ...topLevelFields,
      ...topLevelFields
        .filter(f => f.type === PermitFieldType.Table)
        .flatMap(f => f.values.flat()),
    ]
    return allFields.filter(f => type === f.type)
  }

  private getFieldValuesByType<T>(type: PermitFieldType): T[] {
    return this.fields
      .filter(f => type === f.type)
      .flatMap(({ values }) => values || [])
  }

  public getInspectionFrequency = (permitType: WorkflowType): number => {
    if (!permitType?.isInspectionType) {
      return 0
    }

    return this.specificInspectionFrequency || permitType?.inspectionFrequency
  }

  public getInspectionFrequencyType = (
    permitType: WorkflowType,
  ): CalendricalType => {
    return (
      this.specificInspectionOptions?.inspectionFrequencyType ||
      permitType?.inspectionOptions?.inspectionFrequencyType
    )
  }

  public getSelectedDaysToRepeat = (permitType: WorkflowType): number[] => {
    return (
      this.specificInspectionOptions?.selectedDaysToRepeat ||
      permitType?.inspectionOptions?.selectedDaysToRepeat
    )
  }

  public isApprovalStep = (formType: WorkflowType): boolean => {
    return formType?.workflowSteps.some(
      s =>
        s.id === this.currentWorkflowStepId &&
        s.type === WorkflowStepType.Approval,
    )
  }

  public isReviewStep = (formType: WorkflowType): boolean => {
    return formType?.workflowSteps.some(
      s =>
        s.id === this.currentWorkflowStepId &&
        s.type === WorkflowStepType.Review,
    )
  }

  @computed
  public get materialQuantitiesMap(): { [materialId: string]: number } {
    return this.materialFields.reduce((acc, field) => {
      const instanceId = field.instanceId

      field.values.forEach(({ materialId }, valueIndex) => {
        const quantity = this.getMaterialQuantity(instanceId, valueIndex)
        acc[materialId] = (acc[materialId] ?? 0) + Number(quantity || 0)
      })

      return acc
    }, {})
  }

  @computed
  public get materialLocationMap(): { [materialId: string]: string } {
    return this.materialFields.reduce((acc, field) => {
      const instanceId = field.instanceId

      field.values.forEach(({ materialId }, valueIndex) => {
        const location = this.getMaterialLocation(instanceId, valueIndex)
        acc[materialId] = location?.id
      })

      return acc
    }, {})
  }

  @computed
  public get procurementQuantitiesMap(): { [procurementId: string]: number } {
    return this.materialFields.reduce((acc, field) => {
      const instanceId = field.instanceId

      field.values.forEach(({ procurementId }, valueIndex) => {
        const quantity = this.getMaterialQuantity(instanceId, valueIndex)
        acc[procurementId] = (acc[procurementId] ?? 0) + Number(quantity || 0)
      })

      return acc
    }, {})
  }

  public getMaterialsByIds = (
    materialId: string,
    procurementId: string,
  ): IFormMaterial[] => {
    return this.materials.filter(
      material =>
        material.materialId === materialId &&
        (!procurementId || material.procurementId === procurementId),
    )
  }

  public getMaterialFieldsByIds = (
    materialId: string,
    procurementId: string,
  ): PermitField[] => {
    return this.materialFields.filter(field =>
      field.values.some(
        material =>
          material.materialId === materialId &&
          (!procurementId || material.procurementId === procurementId),
      ),
    )
  }

  public getMaterialLocationIds = (
    materialId: string,
    procurementId: string,
  ): string[] => {
    const set = this.getMaterialFieldsByIds(materialId, procurementId).reduce(
      (set, field) => {
        const instanceId = field.instanceId

        field.values.forEach((material, valueIndex) => {
          if (
            material.materialId !== materialId ||
            (procurementId && material.procurementId !== procurementId)
          ) {
            return
          }

          const location = this.getMaterialLocation(instanceId, valueIndex)
          if (location) {
            set.add(location.id)
          }
        })
        return set
      },
      new Set<string>(),
    )
    return Array.from(set.values())
  }

  public getMaterialQuantity = (
    parentInstanceId: string,
    valueIndex: number,
  ) => {
    const inputNumberFields = this.getAllFieldsByType(
      PermitFieldType.InputNumber,
    )
    return this.getValueByInstanceIdAndValueIndex(
      inputNumberFields,
      parentInstanceId,
      valueIndex,
    )
  }

  public getMaterialLocation = (
    parentInstanceId: string,
    valueIndex: number,
  ) => {
    const locationFields = this.getAllFieldsByType(
      PermitFieldType.LocationInformational,
    )
    return this.getValueByInstanceIdAndValueIndex(
      locationFields,
      parentInstanceId,
      valueIndex,
    )
  }

  private getValueByInstanceIdAndValueIndex(
    fields: PermitField[],
    parentInstanceId: string,
    valueIndex: number,
  ) {
    const field = fields.find(
      f =>
        f.parentInstanceId === parentInstanceId &&
        f.parentValueIndex === valueIndex,
    )
    return field?.values[0]
  }

  public isScheduledWithinRange(
    startDate: Date | number,
    endDate: Date | number,
  ): boolean {
    return areIntervalTimesIntersects(
      { startDate: new Date(this.startDate), endDate: new Date(this.endDate) },
      { startDate: new Date(startDate), endDate: new Date(endDate) },
    )
  }

  private getCurrentWeeklyInspectionDates(
    selectedDaysToRepeat: number[],
    projectDateStore: ProjectDateStore,
    endDateToUse: number | Date,
    inspectionDate: Date,
  ): Date[] {
    const { getWeekDays, getWeekdayNumber, startOfDay } = projectDateStore

    if (!selectedDaysToRepeat?.length) {
      return [inspectionDate]
    }

    return getWeekDays(inspectionDate).filter(
      d =>
        selectedDaysToRepeat?.includes(+getWeekdayNumber(d)) &&
        !isAfter(startOfDay(this.startDate), startOfDay(d)) &&
        !isAfter(d, endDateToUse),
    )
  }

  private getNextWeeklyInspectionDate(
    frequency: number,
    selectedDaysToRepeat: number[],
    projectDateStore: ProjectDateStore,
    inspectionDate: Date,
  ): Date {
    const { startOfWeek, addWeeks } = projectDateStore

    const nextInspectionDate = !selectedDaysToRepeat?.length
      ? inspectionDate
      : startOfWeek(inspectionDate)

    return addWeeks(nextInspectionDate, frequency)
  }

  private getNextMonthlyInspectionDate(
    frequency: number,
    inspectionDate: Date,
    projectDateStore: ProjectDateStore,
  ): Date {
    const { addMonths } = projectDateStore

    return addMonths(inspectionDate, frequency)
  }

  public getEventStyleByColor(color: string = Colors.primary20): EventStyle {
    const style = new EventStyle({
      textColor: Colors.black,
      backgroundColor: Colors.neutral100,
      border: {
        thickness: 0,
        strokeStyle: 'solid',
        color: Colors.neutral100,
      },
    })

    if (this.isRequested || this.isChanged || this.isDraft) {
      style.withTextColor(color).withBorder(1, 'solid', color)
    } else {
      style
        .withBorder(1, 'solid', color)
        .withBackgroundColor(color)
        .withTextColor(color)
    }

    return style
  }

  // It needs for compatibility of data events on
  // (see DesktopCalendar.store.ts : getEventsByColumn)
  // (see CalendarView.store.ts : getSameBlockEvents)
  public codeToDisplay = (): string => {
    return this.code
  }

  public isRequesterOrResponsible = (userId: string): boolean => {
    return (
      this.requesterIds.includes(userId) || this.responsibleIds.includes(userId)
    )
  }

  public isCreator = (userId: string): boolean => {
    return this.createdBy === userId
  }

  public getDateAndTimeToDisplay = (
    projectDateStore: ProjectDateStore,
  ): string => {
    const {
      getYearToDisplay,
      isSameYear,
      getMonthDayYearAndTimeToDisplay,
      isThisYear,
    } = projectDateStore

    if (!isSameYear(this.startDate, this.endDate)) {
      return `${getMonthDayYearAndTimeToDisplay(
        this.startDate,
      )} - ${getMonthDayYearAndTimeToDisplay(this.endDate)}`
    }

    if (!isThisYear(this.startDate)) {
      return `${getYearToDisplay(
        this.startDate,
      )} ${this.getMonthDayAndTimeDatesRange(projectDateStore)}`
    }

    return this.getMonthDayAndTimeDatesRange(projectDateStore)
  }

  public getField = (
    fieldId: string,
    parentInstanceId: string,
    parentValueIndex: number,
  ): PermitField => {
    const key = getWorkflowFieldKey(fieldId, parentInstanceId, parentValueIndex)
    return this.fieldsByIdMap[key]
  }

  private getMonthDayAndTimeDatesRange = (
    projectDateStore: ProjectDateStore,
  ): string => {
    const {
      getWeekdayMonthAndDayToDisplay,
      getMonthDayAndTimeToDisplay,
      getTimeToDisplay,
      isSameDay,
    } = projectDateStore

    if (isSameDay(this.startDate, this.endDate)) {
      return `${getWeekdayMonthAndDayToDisplay(
        this.startDate,
      )}, ${getTimeToDisplay(this.startDate)} - ${getTimeToDisplay(
        this.endDate,
      )}`
    }

    return `${getMonthDayAndTimeToDisplay(
      this.startDate,
    )} - ${getMonthDayAndTimeToDisplay(this.endDate)}`
  }

  @action.bound
  public setStatus(status: SitePermitStatus) {
    this.internalStatus = status
  }

  @action.bound
  public setWorkflowStepId(workflowStepId: string) {
    this.internalCurrentWorkflowStepId = workflowStepId
  }

  @action.bound
  public setWorkflowStepLevel(workflowStepLevel: number) {
    this.internalWorkflowStepLevel = workflowStepLevel
  }

  /**
   * Records the user's sign-off on the step in its current round, replacing one they already gave
   * in that round. A sign-off from an earlier round stays as history. In one-sign-off-per-rule mode
   * a sign-off counts toward one rule, so a user in two rules can hold one per rule.
   */
  @action.bound
  public addSubmission(
    stepId: string,
    userId: string,
    fields: PermitField[] = [],
    ruleId?: string,
  ): void {
    const roundId = this.stepRoundId ?? null
    const existingIndex = this.stepSubmissions.findIndex(
      ss =>
        ss.stepId === stepId &&
        ss.userId === userId &&
        (ss.ruleId ?? null) === (ruleId ?? null) &&
        (ss.roundId ?? null) === roundId,
    )

    const fieldsCopy = fields.map(f => {
      const clone = new PermitField(
        f.instanceId,
        f.fieldId,
        f.type,
        f.parentInstanceId,
        f.parentValueIndex,
      )
      clone.values = [...f.values]
      return clone
    })

    const submission: IStepSubmission = {
      stepId,
      userId,
      ruleId: ruleId ?? null,
      roundId,
      submittedAt: Date.now(),
      fields: fieldsCopy,
    }

    if (existingIndex === -1) {
      this.stepSubmissions.push(submission)
    } else {
      this.stepSubmissions[existingIndex] = submission
    }
  }

  public hasUserSubmitted(stepId: string, userId: string): boolean {
    return this.getLatestRoundSubmissions(stepId).some(
      ss => ss.userId === userId,
    )
  }

  public getSubmissionCount(stepId: string): number {
    return this.getLatestRoundSubmissions(stepId).length
  }

  public getSubmittedUserIds(stepId: string): string[] {
    return this.getLatestRoundSubmissions(stepId).map(ss => ss.userId)
  }

  public getUserSubmission(
    stepId: string,
    userId: string,
  ): IStepSubmission | undefined {
    return this.getLatestRoundSubmissions(stepId).find(
      ss => ss.userId === userId,
    )
  }

  public getRuleSubmission(
    stepId: string,
    ruleId: string,
  ): IStepSubmission | undefined {
    return this.getLatestRoundSubmissions(stepId).find(
      ss => ss.ruleId === ruleId,
    )
  }

  /** Every sign-off ever given on the step, across its rounds. */
  public getStepSubmissions(stepId: string): IStepSubmission[] {
    return this.stepSubmissions.filter(ss => ss.stepId === stepId)
  }

  /**
   * The sign-offs that count for the step: while the workflow waits in it, those of its current
   * round; otherwise those of the last round it was signed in. A workflow that ended in a step
   * (done, denied, failed) no longer waits in it, and the step keeps the sign-offs that ended it.
   */
  public getLatestRoundSubmissions(stepId: string): IStepSubmission[] {
    const stepSubmissions = this.getStepSubmissions(stepId)
    const isWaitingInStep =
      stepId === this.currentWorkflowStepId && !this.isTerminal

    const roundId = isWaitingInStep
      ? (this.stepRoundId ?? null)
      : (stepSubmissions.reduce<IStepSubmission>(
          (latest, ss) =>
            !latest || ss.submittedAt > latest.submittedAt ? ss : latest,
          null,
        )?.roundId ?? null)

    return stepSubmissions.filter(ss => (ss.roundId ?? null) === roundId)
  }
}
